import React from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import { portfolio as t } from "../Themes"
import { Github, Twitter, Facebook, LinkedIn } from "../AllSvgs"
import Me from "../../assets/Images/profile-img.png"

const Section = styled.section`
position: relative;
min-height: 100dvh;
display: flex;
align-items: center;
padding: 8rem clamp(1.25rem, 5vw, 4rem) 4rem;
overflow: hidden;
`

const Grid = styled.div`
width: 100%;
max-width: 1200px;
margin: 0 auto;
display: grid;
grid-template-columns: 1.15fr 0.85fr;
gap: 3rem;
align-items: center;

@media (max-width: 60em){
    grid-template-columns: 1fr;
    gap: 1rem;
    text-align: left;
}
`

const Left = styled.div`
position: relative;
z-index: 2;
`

const Kicker = styled(motion.p)`
font-family: ${t.mono};
color: ${t.accent};
font-size: 0.95rem;
letter-spacing: 0.04em;
margin-bottom: 1.4rem;
`

const Name = styled.h1`
font-size: clamp(2.8rem, 8vw, 5.6rem);
line-height: 0.98;
font-weight: 700;
margin-bottom: 1.2rem;

span{
    display: block;
    overflow: hidden;
}
em{
    font-style: normal;
    color: ${t.muted};
}
`

const Tagline = styled(motion.p)`
max-width: 46ch;
font-size: clamp(1rem, 1.4vw, 1.18rem);
color: ${t.muted};
margin-bottom: 2.2rem;

b{ color: ${t.text}; font-weight: 600; }
`

const Actions = styled(motion.div)`
display: flex;
flex-wrap: wrap;
align-items: center;
gap: 1rem;
`

const Primary = styled.a`
font-family: ${t.mono};
font-size: 0.9rem;
background-color: ${t.accent};
color: #08110d;
padding: 0.85rem 1.6rem;
border-radius: 999px;
font-weight: 600;
transition: all 0.2s ease;

&:hover{ transform: translateY(-2px); box-shadow: 0 10px 30px -10px rgba(${t.accentRgba}, 0.6); }
&:active{ transform: translateY(0); }
`

const Ghost = styled.a`
font-family: ${t.mono};
font-size: 0.9rem;
color: ${t.text};
padding: 0.85rem 1.6rem;
border-radius: 999px;
border: 1px solid ${t.borderHi};
transition: all 0.2s ease;

&:hover{ border-color: ${t.accent}; color: ${t.accent}; }
`

const Socials = styled(motion.div)`
display: flex;
gap: 1.1rem;
margin-top: 2.4rem;

a{
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    border: 1px solid ${t.border};
    transition: all 0.2s ease;
}
a:hover{
    border-color: ${t.accent};
    transform: translateY(-2px);
    background-color: ${t.accentDim};
}
`

const Right = styled.div`
position: relative;
display: grid;
place-items: center;

@media (max-width: 60em){
    order: -1;
    margin-bottom: 1rem;
}
`

const Glow = styled.div`
position: absolute;
width: 78%;
aspect-ratio: 1;
border-radius: 50%;
background: radial-gradient(circle at 50% 45%, rgba(${t.accentRgba}, 0.35), transparent 62%);
filter: blur(10px);
`

const Ring = styled(motion.div)`
position: relative;
width: min(360px, 78vw);
aspect-ratio: 1;
border-radius: 50%;
display: grid;
place-items: end center;
background:
    radial-gradient(circle at 50% 120%, ${t.surfaceHi}, ${t.surface} 60%, transparent 72%);
border: 1px solid ${t.border};
box-shadow: inset 0 0 60px rgba(0,0,0,0.5);
overflow: hidden;

&::before{
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px dashed rgba(${t.accentRgba}, 0.35);
    margin: 10px;
}

img{
    position: relative;
    width: 84%;
    height: auto;
    z-index: 2;
    filter: drop-shadow(0 24px 40px rgba(0,0,0,0.55));
}
`

const Badge = styled(motion.div)`
position: absolute;
z-index: 3;
font-family: ${t.mono};
font-size: 0.74rem;
background-color: rgba(13,15,18,0.82);
backdrop-filter: blur(8px);
border: 1px solid ${t.border};
border-radius: 12px;
padding: 0.55rem 0.85rem;
display: flex;
align-items: center;
gap: 0.5rem;

b{ color: ${t.accent}; }

&.top{ top: 8%; left: -6%; }
&.bottom{ bottom: 10%; right: -4%; }

@media (max-width: 60em){
    &.top{ left: 2%; }
    &.bottom{ right: 2%; }
}
`

const Scroll = styled(motion.a)`
position: absolute;
bottom: 1.6rem;
left: 50%;
transform: translateX(-50%);
font-family: ${t.mono};
font-size: 0.72rem;
color: ${t.faint};
letter-spacing: 0.2em;
text-transform: uppercase;
writing-mode: vertical-rl;
display: flex;
align-items: center;
gap: 0.6rem;

&::after{
    content: "";
    width: 1px;
    height: 38px;
    background: linear-gradient(${t.accent}, transparent);
}

@media (max-width: 60em){ display: none; }
`

const line = (delay) => ({
    initial: { y: "110%" },
    animate: { y: 0 },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay },
})

const fade = (delay) => ({
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: "easeOut", delay },
})

const Hero = () => {
    return (
        <Section id="top">
            <Grid>
                <Left>
                    <Kicker {...fade(0.2)}>Hi, my name is</Kicker>
                    <Name>
                        <span><motion.em {...line(0.3)} style={{ display: "inline-block" }}>Bereket</motion.em></span>
                        <span><motion.span {...line(0.42)} style={{ display: "inline-block" }}>Zelalem.</motion.span></span>
                    </Name>
                    <Tagline {...fade(0.6)}>
                        I'm a <b>Full Stack Developer &amp; VAS Engineer</b> with nearly 5 years
                        building scalable web apps, APIs and telecom-grade digital channels —
                        platforms that serve millions of customers every day.
                    </Tagline>
                    <Actions {...fade(0.72)}>
                        <Primary href="#work">View my work</Primary>
                        <Ghost href="#contact">Get in touch</Ghost>
                    </Actions>
                    <Socials {...fade(0.84)}>
                        <a href="https://github.com/bereket-09" target="_blank" rel="noreferrer" aria-label="GitHub">
                            <Github width={20} height={20} fill={t.text} />
                        </a>
                        <a href="https://www.linkedin.com/in/bereket-zelalem/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                            <LinkedIn width={19} height={19} fill={t.text} />
                        </a>
                        <a href="https://twitter.com/ye_zelalem" target="_blank" rel="noreferrer" aria-label="Twitter">
                            <Twitter width={20} height={20} fill={t.text} />
                        </a>
                        <a href="https://facebook.com/bereket.zelalem.2" target="_blank" rel="noreferrer" aria-label="Facebook">
                            <Facebook width={20} height={20} fill={t.text} />
                        </a>
                    </Socials>
                </Left>

                <Right>
                    <Glow />
                    <Ring
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
                    >
                        <img src={Me} alt="Illustrated portrait of Bereket Zelalem" />
                    </Ring>
                    <Badge className="top" {...fade(1.1)}>
                        <b>●</b> Open to work
                    </Badge>
                    <Badge className="bottom" {...fade(1.25)}>
                        <b>~5</b> years experience
                    </Badge>
                </Right>
            </Grid>

            <Scroll href="#about" {...fade(1.4)}>scroll</Scroll>
        </Section>
    )
}

export default Hero
